import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';

import {
  AffiliatesManagementRepository,
  BetaFlagsRepository,
  BillItemsManagementRepository,
  BrokersManagementRepository,
  HintsRepository,
  LdsConnectionsManagementRepository,
  ProductsManagementRepository,
  ProductsRepository,
  QuoteRepository,
} from '@data-layer/repositories';
import { SharedStateModel } from './shared-state.model';
import {
  FetchAllAffiliates,
  FetchAllBillItems,
  FetchAllBrokers,
  FetchAllDiscountCategories,
  FetchAllHints,
  FetchAllLdsConnections,
  FetchAllQuotes,
  FetchAppBetaFlags,
  FetchDefaultProducts,
  FetchProductsByCompany,
} from './shared.actions';
import { CouponsManagementRepository } from '@data-layer/repositories/coupons-management.repository';
import { ProductDetailsModel, ShortModel } from '@data-layer/models/common';
import { Observable, throwError } from 'rxjs';
import { UserBetaFlagModel } from '@data-layer/models/user';

const defaults: SharedStateModel = {
  brokers: [],
  affiliates: [],
  ldsConnections: [],
  defaultProducts: [],
  quotes: [],
  billItems: [],
  hints: [],
  productsByCompany: [],
  productsByOrganization: [],
  productCategories: [],
  appBetaFlags: [],
};

@State<SharedStateModel>({
  name: 'shared',
  defaults,
})
@Injectable()
export class SharedState {
  constructor(
    private brokersManagementRepository: BrokersManagementRepository,
    private affiliateManagementRepository: AffiliatesManagementRepository,
    private ldsConnectionsManagementRepository: LdsConnectionsManagementRepository,
    private productsManagementRepository: ProductsManagementRepository,
    private quoteManagementRepository: QuoteRepository,
    private billItemsManagementRepository: BillItemsManagementRepository,
    private hintsRepository: HintsRepository,
    private productsRepository: ProductsRepository,
    private couponsManagementRepository: CouponsManagementRepository,
    private betaFlagsRepository: BetaFlagsRepository,
  ) {}

  @Action(FetchAllBrokers)
  public fetchAllBrokers(ctx: StateContext<SharedStateModel>) {
    return this.brokersManagementRepository.search({}).pipe(tap((response) => ctx.patchState({ brokers: response.items })));
  }

  @Action(FetchAllAffiliates)
  public fetchAllAffiliates(ctx: StateContext<SharedStateModel>) {
    return this.affiliateManagementRepository.search({}).pipe(tap((response) => ctx.patchState({ affiliates: response.items })));
  }

  @Action(FetchAllLdsConnections)
  public fetchAllLdsConnections(ctx: StateContext<SharedStateModel>) {
    return this.ldsConnectionsManagementRepository.search({}).pipe(tap((response) => ctx.patchState({ ldsConnections: response.items })));
  }

  @Action(FetchDefaultProducts)
  public fetchDefaultProducts(ctx: StateContext<SharedStateModel>) {
    return this.productsManagementRepository.search({}).pipe(tap((response) => ctx.patchState({ defaultProducts: response.items })));
  }

  @Action(FetchAllQuotes)
  public fetchAllQuotes(ctx: StateContext<SharedStateModel>) {
    return this.quoteManagementRepository.searchManagement({}).pipe(tap((response) => ctx.patchState({ quotes: response.items })));
  }

  @Action(FetchAllBillItems)
  public fetchAllBillItems(ctx: StateContext<SharedStateModel>) {
    return this.billItemsManagementRepository.search({}).pipe(tap((response) => ctx.patchState({ billItems: response.items })));
  }

  @Action(FetchAllHints)
  public fetchAllHints(ctx: StateContext<SharedStateModel>) {
    return this.hintsRepository.search().pipe(tap((response) => ctx.patchState({ hints: response })));
  }

  @Action(FetchAppBetaFlags)
  public fetchAppBetaFlags(ctx: StateContext<SharedStateModel>): Observable<UserBetaFlagModel[]> {
    return this.betaFlagsRepository
      .getAppBetaFeatures()
      .pipe(tap((response: UserBetaFlagModel[]) => ctx.patchState({ appBetaFlags: response })));
  }

  @Action(FetchProductsByCompany)
  public fetchProductsByCompany(ctx: StateContext<SharedStateModel>, { payload }: FetchProductsByCompany) {
    return this.productsRepository
      .getProductsByCompany(payload.productCode, payload.companyId, payload.countryId, payload.organizationId)
      .pipe(
        tap((product) => {
          // probably should be moved to any shared state later

          const state: SharedStateModel = ctx.getState();
          // #13318 — a price fetched without a company belongs to the organization and is kept
          // apart, so it cannot evict the price another consumer holds for a real company
          const isByOrganization: boolean = !payload.companyId;
          const source: ProductDetailsModel[] = isByOrganization ? state.productsByOrganization : state.productsByCompany;
          const result: ProductDetailsModel[] = [...source];
          const index: number = result.findIndex((item: ProductDetailsModel) => item.productCode === payload.productCode);
          const entry: ProductDetailsModel = {
            productCode: payload.productCode,
            product,
            companyId: payload.companyId,
            organizationId: payload.organizationId ?? null,
          };

          if (index !== -1) {
            result[index] = entry;
          } else {
            result.push(entry);
          }

          ctx.patchState(isByOrganization ? { productsByOrganization: result } : { productsByCompany: result });
        }),
        catchError((error: Error) => {
          // #13213 — drop the stale entry so consumers hide the price instead of showing an outdated one
          const state: SharedStateModel = ctx.getState();
          const isByOrganization: boolean = !payload.companyId;
          const source: ProductDetailsModel[] = isByOrganization ? state.productsByOrganization : state.productsByCompany;
          const rest: ProductDetailsModel[] = source.filter((item: ProductDetailsModel) => item.productCode !== payload.productCode);
          ctx.patchState(isByOrganization ? { productsByOrganization: rest } : { productsByCompany: rest });
          return throwError(error);
        }),
      );
  }

  @Action(FetchAllDiscountCategories)
  public fetchAllDiscountCategories(ctx: StateContext<SharedStateModel>): Observable<ShortModel[]> {
    return this.couponsManagementRepository
      .getDiscountCategories(false)
      .pipe(tap((productCategories: ShortModel[]) => ctx.patchState({ productCategories })));
  }
}
